/**
 * sl-dl66.3.5: PendingIntervalChangeBanner — Shows scheduled interval changes
 * with cancel capability (§356 BGB Widerrufsrecht).
 *
 * Queries pending interval changes for plan, seat addon, and credit subs.
 * Displays info banners with cancel buttons for each pending change.
 */
import { Button, notify } from '@affine/component';
import { GraphQLService } from '@affine/core/modules/cloud';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  cancelPendingIntervalChangeMutation,
  pendingIntervalChangesQuery,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useState } from 'react';

interface PendingChange {
  type: string;
  newInterval: string;
  effectiveDate: string;
  creditPackId?: string | null;
}

const bannerStyle: CSSProperties = {
  padding: '12px 16px',
  marginBottom: '12px',
  backgroundColor: 'var(--streamlain-background-warning-color, #fff8e1)',
  border: '1px solid var(--streamlain-border-color)',
  borderRadius: '8px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '12px',
  fontSize: '14px',
  lineHeight: '1.5',
};

const bannerTextStyle: CSSProperties = {
  flex: 1,
  color: 'var(--streamlain-text-primary-color)',
};

export const PendingIntervalChangeBanner = () => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const graphqlService = useService(GraphQLService);
  const [pendingChanges, setPendingChanges] = useState<PendingChange[]>([]);
  const [cancellingType, setCancellingType] = useState<string | null>(null);

  const fetchPendingChanges = useCallback(async () => {
    if (!workspace) return;
    try {
      const result = await graphqlService.gql({
        query: pendingIntervalChangesQuery,
        variables: { workspaceId: workspace.id },
      });
      setPendingChanges(result.pendingIntervalChanges ?? []);
    } catch {
      // Silently ignore — banner just won't show
    }
  }, [graphqlService, workspace]);

  useEffect(() => {
    void fetchPendingChanges();
  }, [fetchPendingChanges]);

  const handleCancel = useCallback(
    async (change: PendingChange) => {
      if (!workspace) return;
      setCancellingType(change.type);
      try {
        await graphqlService.gql({
          query: cancelPendingIntervalChangeMutation,
          variables: {
            workspaceId: workspace.id,
            type: change.type,
            creditPackId: change.creditPackId ?? undefined,
          },
        });
        notify.success({
          title: t['com.streamlain.payment.billing.pending-change-cancelled'](),
        });
        // Remove the cancelled change from state
        setPendingChanges(prev =>
          prev.filter(
            c =>
              !(
                c.type === change.type && c.creditPackId === change.creditPackId
              )
          )
        );
      } catch {
        notify.error({
          title:
            t['com.streamlain.payment.billing.pending-change-cancel-failed'](),
        });
      } finally {
        setCancellingType(null);
      }
    },
    [graphqlService, t, workspace]
  );

  if (pendingChanges.length === 0) {
    return null;
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'plan':
        return t['com.streamlain.payment.billing.pending-type-plan']();
      case 'seat':
        return t['com.streamlain.payment.billing.pending-type-seat']();
      case 'credit':
        return t['com.streamlain.payment.billing.pending-type-credit']();
      default:
        return type;
    }
  };

  const getIntervalLabel = (interval: string) => {
    return interval === 'month'
      ? t['com.streamlain.payment.billing.pending-interval-monthly']()
      : t['com.streamlain.payment.billing.pending-interval-yearly']();
  };

  return (
    <>
      {pendingChanges.map(change => (
        <div
          key={`${change.type}-${change.creditPackId ?? ''}`}
          style={bannerStyle}
          data-testid={`pending-interval-change-${change.type}`}
        >
          <div style={bannerTextStyle}>
            <strong>{getTypeLabel(change.type)}:</strong>{' '}
            {t['com.streamlain.payment.billing.pending-change-banner']({
              interval: getIntervalLabel(change.newInterval),
              date: new Date(change.effectiveDate).toLocaleDateString(),
            })}
          </div>
          <Button
            variant="error"
            onClick={() => void handleCancel(change)}
            disabled={cancellingType === change.type}
            data-testid={`cancel-pending-change-${change.type}`}
          >
            {t['com.streamlain.payment.billing.pending-change-cancel']()}
          </Button>
        </div>
      ))}
    </>
  );
};
